import { ExceptionsState, TkSummary } from "../common/types"
import { getBooleanCookie, getCookie, getIntCookie, setCookie } from "../common/ts-utils"
import { ExceptionAction } from "../actions/exceptions"
import { WorkRuleSetRule } from "@rhumbix/rmbx_work_rules"

const initialState: ExceptionsState = {
    filterExceptions: getCookie("filter_exceptions") === "true",
    cicoDiffException: {
        enabled: getCookie("cicoDiffException") === "true",
        value: getCookie("exception_threshold") || "",
        units: (getCookie("exception_units") as "hours" | "percent") || "hours",
        flagAnyEdits: getBooleanCookie("cico_exception_flag_any_edits", false),
        flagOthersEdits: getBooleanCookie("cico_exception_flag_others_edits", false),
        ignoreNoCico: getBooleanCookie("cico_exception_ignore_no_cico", false),
    },
    hoursException: {
        enabled: getCookie("hoursException") === "true",
        minHours: getCookie("min_hours_exception_threshold") || "",
        maxHours: getCookie("max_hours_exception_threshold") || "",
        weekly: getBooleanCookie("hours_exception_weekly", false),
        minSTHours: getIntCookie("hours_exception_min_st_hours", 40),
        minSTHoursEnabled: getBooleanCookie("hours_exception_min_st_hours_enabled", false),
    },
    shiftsBreaksException: {
        enabled: getCookie("shiftsBreaksException") === "true",
        // Cookies cannot store booleans, just strings. So we store "true" or "false" and convert it to boolean.
        // The default is also true if no cookie has been set.
        missingShift: getBooleanCookie("shift_break_exception_missing_shift", true),
        missingMeal: getBooleanCookie("shift_break_exception_missing_meal", true),
        missingBreak: getBooleanCookie("shift_break_exception_missing_break", true),
        minMealDurationEnabled: getBooleanCookie("shift_break_exception_min_meal_duration_enabled", true),
        minMealDuration: getIntCookie("shift_break_exception_min_meal_duration", 30),
        maxTimeBeforeMealEnabled: getBooleanCookie("shift_break_exception_max_time_before_meal_enabled", true),
        maxTimeBeforeMeal: getIntCookie("shift_break_exception_max_time_before_meal", 300),
    },
    workRulesException: {
        enabled: getCookie("workRulesException") === "true",
        workRuleOverride: JSON.parse(getCookie("workRuleOverride") || "[]") as WorkRuleSetRule[],
    },
    tkSummary: null,
    tkSummaryQueryString: null,
}

const exceptions = (state = initialState, action: ExceptionAction): ExceptionsState => {
    switch (action.type) {
        case "TOGGLE_EXCEPTION":
            setCookie(action.whichException, state[action.whichException].enabled ? "false" : "true", "/", undefined)
            return {
                ...state,
                [action.whichException]: {
                    ...state[action.whichException],
                    enabled: !state[action.whichException].enabled,
                },
            }
        case "SET_EXCEPTION_VALUE":
            return {
                ...state,
                ...action.exception,
            }
        case "TOGGLE_FILTER_EXCEPTIONS":
            return {
                ...state,
                filterExceptions: !state.filterExceptions,
            }
        case "SET_TK_SUMMARY":
            return {
                ...state,
                tkSummary: action.data,
                tkSummaryQueryString: action.queryString,
            }
        case "MERGE_TK_SUMMARY": {
            // Clear the requested employees first so an employee who now has no rows (the
            // endpoint omits employees with no data) doesn't keep stale rows, then overlay
            // the fetched rows. Non-requested employees are left untouched.
            const merged: TkSummary = { ...(state.tkSummary ?? {}) }
            action.employeeIds.forEach(id => {
                merged[String(id)] = []
            })
            Object.assign(merged, action.data)
            return {
                ...state,
                tkSummary: merged,
            }
        }
        case "SET_WORK_RULE_OVERRIDE":
            return {
                ...state,
                workRulesException: {
                    ...state.workRulesException,
                    workRuleOverride: action.data,
                },
            }
        case "CLEAR_WORK_RULE_OVERRIDE":
            return {
                ...state,
                workRulesException: {
                    ...state.workRulesException,
                    workRuleOverride: [],
                },
            }
        default:
            return state
    }
}

export default exceptions
